import React, { useState, useMemo, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  SurveyDefinition,
  SurveySection,
  QuestionDefinition,
  QuestionType,
  QuestionTemplate,
} from '../../types/survey.types';
import QuestionEditorModal from './QuestionEditorModal';
import ConfirmDialog from '../common/ConfirmDialog';
import ImportPreview from './ImportPreview';
import BulkLogicEditor from './BulkLogicEditor';
import { deepClone } from '../../utils/deepClone';
import { parseCSV, parseDocument, parseDOCX, convertToQuestionDefinitions, ParsedQuestion } from '../../utils/surveyImport';

// Question Categories with Templates
interface QuestionCategory {
  id: string;
  name: string;
  icon: string;
  templates: QuestionTemplate[];
}

const questionCategories: QuestionCategory[] = [
  {
    id: 'text-inputs',
    name: 'Text Inputs',
    icon: '📝',
    templates: [
      {
        id: 'text',
        name: 'Text Input',
        type: 'text' as QuestionType,
        icon: '📝',
        description: 'Single line text',
        defaultSettings: {
          id: '',
          type: 'text' as QuestionType,
          title: 'Enter your question here',
          required: false,
          validationRules: [],
          settings: {
            maxLength: 500,
          },
        },
      },
      {
        id: 'textarea',
        name: 'Text Area',
        type: 'textarea' as QuestionType,
        icon: '📄',
        description: 'Multi-line text',
        defaultSettings: {
          id: '',
          type: 'textarea' as QuestionType,
          title: 'Enter your question here',
          required: false,
          validationRules: [],
          settings: {
            rows: 4,
            maxLength: 2000,
          },
        },
      },
      {
        id: 'email',
        name: 'Email',
        type: 'email' as QuestionType,
        icon: '📧',
        description: 'Email address',
        defaultSettings: {
          id: '',
          type: 'email' as QuestionType,
          title: 'Enter your email address',
          required: false,
          validationRules: [
            { type: 'email', message: 'Please enter a valid email address' },
          ],
        },
      },
      {
        id: 'phone',
        name: 'Phone Number',
        type: 'phone' as QuestionType,
        icon: '📱',
        description: 'Phone number',
        defaultSettings: {
          id: '',
          type: 'phone' as QuestionType,
          title: 'Enter your phone number',
          required: false,
          validationRules: [
            { type: 'pattern', value: '^[0-9+\\-\\s()]+$', message: 'Please enter a valid phone number' },
          ],
        },
      },
    ],
  },
  {
    id: 'multiple-choice',
    name: 'Multiple Choice',
    icon: '🔘',
    templates: [
      {
        id: 'radio',
        name: 'Multiple Choice',
        type: 'radio' as QuestionType,
        icon: '🔘',
        description: 'Single selection',
        defaultSettings: {
          id: '',
          type: 'radio' as QuestionType,
          title: 'Enter your question here',
          required: false,
          validationRules: [],
          options: [
            { id: 'opt1', label: 'Option 1', value: 'option1' },
            { id: 'opt2', label: 'Option 2', value: 'option2' },
            { id: 'opt3', label: 'Option 3', value: 'option3' },
          ],
          settings: {
            layout: 'vertical',
          },
        },
      },
      {
        id: 'checkbox',
        name: 'Checkboxes',
        type: 'checkbox' as QuestionType,
        icon: '☑️',
        description: 'Multiple selections',
        defaultSettings: {
          id: '',
          type: 'checkbox' as QuestionType,
          title: 'Select all that apply',
          required: false,
          validationRules: [],
          options: [
            { id: 'opt1', label: 'Option 1', value: 'option1' },
            { id: 'opt2', label: 'Option 2', value: 'option2' },
            { id: 'opt3', label: 'Option 3', value: 'option3' },
          ],
          settings: {
            layout: 'vertical',
          },
        },
      },
      {
        id: 'dropdown',
        name: 'Dropdown',
        type: 'dropdown' as QuestionType,
        icon: '📋',
        description: 'Dropdown menu',
        defaultSettings: {
          id: '',
          type: 'dropdown' as QuestionType,
          title: 'Choose from the list',
          required: false,
          validationRules: [],
          options: [
            { id: 'opt1', label: 'Option 1', value: 'option1' },
            { id: 'opt2', label: 'Option 2', value: 'option2' },
            { id: 'opt3', label: 'Option 3', value: 'option3' },
          ],
        },
      },
      {
        id: 'multiselect',
        name: 'Multi-Select Dropdown',
        type: 'multiselect' as QuestionType,
        icon: '📑',
        description: 'Multiple choice dropdown',
        defaultSettings: {
          id: '',
          type: 'multiselect' as QuestionType,
          title: 'Select all that apply',
          required: false,
          validationRules: [],
          options: [
            { id: 'opt1', label: 'Option 1', value: 'option1' },
            { id: 'opt2', label: 'Option 2', value: 'option2' },
            { id: 'opt3', label: 'Option 3', value: 'option3' },
          ],
        },
      },
    ],
  },
  {
    id: 'ratings-scales',
    name: 'Ratings & Scales',
    icon: '📊',
    templates: [
      {
        id: 'rating',
        name: 'Rating Scale',
        type: 'rating' as QuestionType,
        icon: '⭐',
        description: 'Star rating',
        defaultSettings: {
          id: '',
          type: 'rating' as QuestionType,
          title: 'Rate your experience',
          required: false,
          validationRules: [],
          settings: {
            minValue: 1,
            maxValue: 5,
            scaleLabels: ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'],
          },
        },
      },
      {
        id: 'scale',
        name: 'Likert Scale',
        type: 'scale' as QuestionType,
        icon: '📊',
        description: 'Agreement scale',
        defaultSettings: {
          id: '',
          type: 'scale' as QuestionType,
          title: 'How much do you agree?',
          required: false,
          validationRules: [],
          settings: {
            minValue: 1,
            maxValue: 5,
            scaleLabels: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
          },
        },
      },
    ],
  },
  {
    id: 'clinical',
    name: 'Clinical',
    icon: '💊',
    templates: [
      {
        id: 'dosing_schedule',
        name: 'Dosing Schedule',
        type: 'dosing_schedule' as QuestionType,
        icon: '💊',
        description: 'Dosing protocol with days on/off and duration',
        defaultSettings: {
          id: '',
          type: 'dosing_schedule' as QuestionType,
          title: 'Dosing Protocol (Schedule)',
          description: 'Please describe your current dosing schedule for this product.',
          required: false,
          validationRules: [],
          settings: {
            includeDoseAmount: true,
            availableDoseUnits: ['mL', 'IU', 'mcg'],
            maxCycleDuration: 52,
            defaultCycleUnit: 'weeks',
          },
        },
      },
      {
        id: 'measurement',
        name: 'Dose / Strength',
        type: 'measurement' as QuestionType,
        icon: '💉',
        description: 'Dose amount with unit (e.g., 250 mg)',
        defaultSettings: {
          id: '',
          type: 'measurement' as QuestionType,
          title: 'What is your current dose / strength?',
          required: false,
          validationRules: [],
          settings: {
            measurementUnits: ['mg', 'mL', 'g', 'IU', 'mcg', 'units'],
            defaultMeasurementUnit: 'mg',
          },
        },
      },
    ],
  },
  {
    id: 'other',
    name: 'Other',
    icon: '➕',
    templates: [
      {
        id: 'yesno',
        name: 'Yes/No',
        type: 'yesno' as QuestionType,
        icon: '✅',
        description: 'Yes or no question',
        defaultSettings: {
          id: '',
          type: 'yesno' as QuestionType,
          title: 'Yes or No?',
          required: false,
          validationRules: [],
          options: [
            { id: 'yes', label: 'Yes', value: 'yes' },
            { id: 'no', label: 'No', value: 'no' },
          ],
        },
      },
      {
        id: 'number',
        name: 'Number',
        type: 'number' as QuestionType,
        icon: '🔢',
        description: 'Numeric input',
        defaultSettings: {
          id: '',
          type: 'number' as QuestionType,
          title: 'Enter a number',
          required: false,
          validationRules: [],
          settings: {
            minValue: 0,
            maxValue: 100,
            step: 1,
          },
        },
      },
      {
        id: 'date',
        name: 'Date Picker',
        type: 'date' as QuestionType,
        icon: '📅',
        description: 'Date selection',
        defaultSettings: {
          id: '',
          type: 'date' as QuestionType,
          title: 'Select a date',
          required: false,
          validationRules: [],
          settings: {
            minDate: '',
            maxDate: '',
          },
        },
      },
      {
        id: 'file',
        name: 'File Upload',
        type: 'file' as QuestionType,
        icon: '📎',
        description: 'File upload',
        defaultSettings: {
          id: '',
          type: 'file' as QuestionType,
          title: 'Upload a file',
          required: false,
          validationRules: [],
        },
      },
      {
        id: 'signature',
        name: 'Digital Signature',
        type: 'signature' as QuestionType,
        icon: '✍️',
        description: 'Signature pad',
        defaultSettings: {
          id: '',
          type: 'signature' as QuestionType,
          title: 'Please sign here',
          required: false,
          validationRules: [],
        },
      },
      {
        id: 'matrix',
        name: 'Matrix/Grid',
        type: 'matrix' as QuestionType,
        icon: '⊞',
        description: 'Grid questions',
        defaultSettings: {
          id: '',
          type: 'matrix' as QuestionType,
          title: 'Rate the following',
          required: false,
          validationRules: [],
        },
      },
      {
        id: 'ranking',
        name: 'Ranking',
        type: 'ranking' as QuestionType,
        icon: '🔢',
        description: 'Rank items in order',
        defaultSettings: {
          id: '',
          type: 'ranking' as QuestionType,
          title: 'Rank the following in order of preference',
          required: false,
          validationRules: [],
          options: [
            { id: 'opt1', label: 'Option 1', value: 'option1' },
            { id: 'opt2', label: 'Option 2', value: 'option2' },
            { id: 'opt3', label: 'Option 3', value: 'option3' },
          ],
        },
      },
    ],
  },
];

interface SurveyBuilderProps {
  survey?: Partial<SurveyDefinition>;
  onSave: (survey: Partial<SurveyDefinition>, options?: { silent?: boolean }) => Promise<string | void>;
  onCancel: () => void;
}

export interface SurveyBuilderRef {
  save: () => void;
}

interface DraggableQuestionProps {
  question: QuestionDefinition;
  sectionId: string;
  questionNumber: number;
  onEdit: (questionId: string) => void;
  onDelete: (questionId: string) => void;
  getQuestionIcon: (type: QuestionType) => string;
  isJustDropped?: boolean;
  animationIndex?: number;
}

const DraggableQuestion: React.FC<DraggableQuestionProps> = ({
  question,
  sectionId,
  questionNumber,
  onEdit,
  onDelete,
  getQuestionIcon,
  isJustDropped = false,
  animationIndex = 0,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: question.id,
    data: {
      type: 'question',
      sectionId,
      question,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Get descriptive question type name
  const getQuestionTypeName = (type: QuestionType): string => {
    switch (type) {
      case 'text':
        return 'Single Line Text';
      case 'textarea':
        return 'Multi-Line Text';
      case 'number':
        return 'Number Input';
      case 'email':
        return 'Email Input';
      case 'date':
        return 'Date Picker';
      case 'radio':
        return 'Multiple Choice';
      case 'checkbox':
        return 'Checkboxes';
      case 'dropdown':
        return 'Dropdown';
      case 'rating':
        return 'Star Rating';
      case 'scale':
        return 'Likert Scale';
      case 'yesno':
        return 'Yes/No';
      default:
        return type;
    }
  };

  // Get badge color class based on type
  const getBadgeClass = (type: QuestionType): string => {
    if (['text', 'textarea', 'email', 'number', 'date'].includes(type)) {
      return 'text-input';
    }
    if (['radio', 'checkbox', 'dropdown'].includes(type)) {
      return 'multiple-choice';
    }
    if (['rating', 'scale'].includes(type)) {
      return 'rating';
    }
    return 'other';
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={{...style, cursor: isDragging ? 'grabbing' : 'grab'}}
        className={`question-card ${isDragging ? 'dragging' : ''} ${
          isJustDropped ? 'question-just-dropped' : 'question-card-enter'
        }`}
        {...attributes}
        {...listeners}
      >
        <div className="flex items-start gap-4">
          {/* Question Number Badge */}
          <div className="question-number">
            {questionNumber}
          </div>

          {/* Question Content */}
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-lg mb-1 truncate" style={{ color: 'var(--color-primary)' }}>
              {question.title || 'Untitled Question'}
            </h4>

            {/* Type Badge & Required */}
            <div className="flex items-center gap-2 mb-2">
              <span className={`question-type-badge ${getBadgeClass(question.type)}`}>
                {getQuestionTypeName(question.type)}
              </span>
              {question.required && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full border border-red-200">
                  <span>*</span>
                  Required
                </span>
              )}
            </div>

            {/* Description */}
            {question.description && (
              <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                {question.description}
              </p>
            )}

            {/* Options Preview for choice questions */}
            {question.options && question.options.length > 0 && (
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span>{question.options.length} options</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(question.id);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              className="action-button edit"
              title="Edit question"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              className="action-button delete"
              title="Delete question"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Question"
        message={`Are you sure you want to delete "${question.title}"? This action cannot be undone.`}
        confirmText="Delete Question"
        cancelText="Cancel"
        onConfirm={() => {
          onDelete(question.id);
          setShowDeleteConfirm(false);
        }}
        onCancel={() => setShowDeleteConfirm(false)}
        type="danger"
      />
    </>
  );
};

const SurveyBuilder = forwardRef<SurveyBuilderRef, SurveyBuilderProps>(({
  survey: initialSurvey,
  onSave,
  onCancel,
}, ref) => {
  const [survey, setSurvey] = useState<Partial<SurveyDefinition>>({
    title: 'New Survey',
    description: '',
    instructions: '',
    settings: {
      isActive: false,
      allowAnonymous: true,
      requireAuth: false,
      allowMultipleResponses: false,
      showProgressBar: true,
      collectEmail: false,
      collectIpAddress: true,
      accessType: 'public',
    },
    sections: [
      {
        id: 'section1',
        title: 'Section 1',
        description: '',
        questions: [],
        order: 0,
      },
    ],
    ...initialSurvey,
  });

  const [selectedSection, setSelectedSection] = useState<string>('section1');
  const [editingQuestion, setEditingQuestion] = useState<QuestionDefinition | null>(null);
  const [showQuestionEditor, setShowQuestionEditor] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['text-inputs']); // Start with first category expanded
  const [isDragging, setIsDragging] = useState(false);
  const [draggingType, setDraggingType] = useState<string | null>(null);
  const [isDragOverMainArea, setIsDragOverMainArea] = useState(false);
  const [justDroppedId, setJustDroppedId] = useState<string | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingSectionTitle, setEditingSectionTitle] = useState<string>('');
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [parsedImportQuestions, setParsedImportQuestions] = useState<ParsedQuestion[]>([]);
  const [showImportMenu, setShowImportMenu] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [deletingSectionId, setDeletingSectionId] = useState<string | null>(null);
  const [showBulkLogicEditor, setShowBulkLogicEditor] = useState(false);
  const importButtonRef = useRef<HTMLButtonElement>(null);
  const [showDescription, setShowDescription] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialMount = useRef(true);
  const isSavingRef = useRef(false); // Track if a save operation is in progress

  // Expose save method to parent component
  useImperativeHandle(ref, () => ({
    save: () => {
      onSave(survey);
    }
  }));

  // Autosave effect - saves after 1 second of inactivity
  useEffect(() => {
    // Skip autosave on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Skip autosave if a save is already in progress (prevents duplicate surveys)
    if (isSavingRef.current) {
      return;
    }

    // Skip autosave if survey already has an ID (already saved once)
    // This check uses the current survey state which may not have the ID yet
    // The real protection is isSavingRef above

    // Check if survey has at least one question before autosaving
    const hasQuestions = survey.sections?.some(section => section.questions.length > 0) || false;
    const hasTitle = survey.title && survey.title.trim().length > 0;

    if (!hasQuestions || !hasTitle) {
      // Don't autosave if there are no questions or no title yet
      return;
    }

    // Clear existing timer
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    // Set new timer for autosave (5 second debounce to avoid constant saving)
    autosaveTimerRef.current = setTimeout(async () => {
      // Double-check save isn't in progress when timer fires
      if (isSavingRef.current) {
        return;
      }

      isSavingRef.current = true;
      try {
        const newSurveyId = await onSave(survey, { silent: true });

        // If a new survey was created, update local state with the ID immediately
        // This prevents duplicate surveys on subsequent autosaves
        if (newSurveyId && !survey.id) {
          setSurvey(prev => ({ ...prev, id: newSurveyId }));
        }
        setSaveStatus('saved');
      } catch {
        setSaveStatus('idle');
      } finally {
        isSavingRef.current = false;
      }
    }, 5000);

    // Cleanup on unmount
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [survey, onSave]);

  // Sync internal state when initialSurvey prop changes (e.g., when editing a different survey)
  useEffect(() => {
    if (initialSurvey && initialSurvey.id) {
      setSurvey(prev => ({
        ...prev,
        ...initialSurvey,
      }));
      // Also reset to first section when loading a new survey
      if (initialSurvey.sections && initialSurvey.sections.length > 0) {
        setSelectedSection(initialSurvey.sections[0].id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSurvey?.id]); // Only re-run when the survey ID changes

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Helper function to clean HTML from contentEditable - keeps only text and <br> tags
  const cleanDescriptionHTML = (html: string): string => {
    let cleaned = html;

    // Replace &nbsp; with regular spaces
    cleaned = cleaned.replace(/&nbsp;/g, ' ');

    // Convert block-level elements (p, div) to <br> before stripping
    cleaned = cleaned.replace(/<\/p>\s*<p[^>]*>/gi, '<br>');
    cleaned = cleaned.replace(/<\/?p[^>]*>/gi, '<br>');
    cleaned = cleaned.replace(/<\/?div[^>]*>/gi, '<br>');

    // Remove all remaining HTML tags except <br>
    cleaned = cleaned.replace(/<(?!\/?br\s*\/?>)[^>]+>/gi, '');

    // Clean up excessive whitespace on each line
    cleaned = cleaned.split('<br>').map(line => line.replace(/\s+/g, ' ').trim()).join('<br>');

    // Remove empty lines (consecutive <br> tags)
    cleaned = cleaned.replace(/(<br\s*\/?>){3,}/gi, '<br><br>');

    // Remove leading/trailing <br> tags
    cleaned = cleaned.replace(/^(<br\s*\/?>)+/gi, '').replace(/(<br\s*\/?>)+$/gi, '');

    return cleaned.trim();
  };

  // Initialize description content on mount and when survey changes
  useEffect(() => {
    if (descriptionRef.current) {
      const storedValue = survey.description || '';

      if (descriptionRef.current.innerHTML !== storedValue) {
        descriptionRef.current.innerHTML = storedValue;
      }
    }
  }, [survey.description]);

  const currentSection = useMemo(() => {
    return survey.sections?.find(s => s.id === selectedSection);
  }, [survey.sections, selectedSection]);

  const totalQuestions = useMemo(() => {
    return survey.sections?.reduce((total, section) => total + section.questions.length, 0) || 0;
  }, [survey.sections]);

  const getQuestionIcon = (type: QuestionType): string => {
    // Find template across all categories
    for (const category of questionCategories) {
      const template = category.templates.find((t: QuestionTemplate) => t.type === type);
      if (template) return template.icon;
    }
    return '❓';
  };

  const handleDragStart = (event: DragStartEvent) => {
    // Optional: Add visual feedback when dragging starts
  };

  const handleDragOver = (event: DragOverEvent) => {
    // Handle dragging over different containers
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // Handle reordering questions within a section
    if (activeData?.type === 'question' && overData?.type === 'question') {
      const sectionId = activeData.sectionId;
      const section = survey.sections?.find(s => s.id === sectionId);

      if (section) {
        const oldIndex = section.questions.findIndex(q => q.id === active.id);
        const newIndex = section.questions.findIndex(q => q.id === over.id);

        if (oldIndex !== newIndex) {
          const updatedQuestions = arrayMove(section.questions, oldIndex, newIndex);

          setSurvey(prev => ({
            ...prev,
            sections: prev.sections?.map(s =>
              s.id === sectionId
                ? { ...s, questions: updatedQuestions }
                : s
            ),
          }));
        }
      }
    }

    // Handle adding new question from template
    if (activeData?.type === 'template' && overData?.type === 'section') {
      const template = activeData.template as QuestionTemplate;
      const sectionId = overData.sectionId;

      addQuestionToSection(template, sectionId);
    }
  };

  const addQuestionToSection = (template: QuestionTemplate, sectionId: string) => {
    const newQuestion: QuestionDefinition = {
      ...template.defaultSettings,
      id: `question_${Date.now()}`,
      type: template.type,
      title: template.defaultSettings.title || template.name,
      required: template.defaultSettings.required || false,
      validationRules: template.defaultSettings.validationRules || [],
    };

    setSurvey(prev => ({
      ...prev,
      sections: prev.sections?.map(section =>
        section.id === sectionId
          ? {
              ...section,
              questions: [...section.questions, newQuestion],
            }
          : section
      ),
    }));

    // Trigger celebration animation
    setJustDroppedId(newQuestion.id);
    setTimeout(() => setJustDroppedId(null), 300);
  };

  const editQuestion = (questionId: string) => {
    const question = currentSection?.questions.find(q => q.id === questionId);
    if (question) {
      // Deep clone to preserve nested arrays like logicRules and Date objects
      setEditingQuestion(deepClone(question));
      setShowQuestionEditor(true);
    }
  };

  const deleteQuestion = (questionId: string) => {
    // Confirmation is already handled by QuestionEditorModal's custom ConfirmDialog
    setSurvey(prev => ({
      ...prev,
      sections: prev.sections?.map(section =>
        section.id === selectedSection
          ? {
              ...section,
              questions: section.questions.filter(q => q.id !== questionId),
            }
          : section
      ),
    }));
  };

  const saveQuestion = (updatedQuestion: QuestionDefinition) => {
    setSurvey(prev => ({
      ...prev,
      sections: prev.sections?.map(section =>
        section.id === selectedSection
          ? {
              ...section,
              questions: section.questions.map(q =>
                q.id === updatedQuestion.id ? updatedQuestion : q
              ),
            }
          : section
      ),
    }));

    setEditingQuestion(null);
    setShowQuestionEditor(false);
  };

  // Category management functions
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const isCategoryExpanded = (categoryId: string) => {
    return expandedCategories.includes(categoryId);
  };

  const toggleAllCategories = () => {
    if (expandedCategories.length === questionCategories.length) {
      setExpandedCategories([]);
    } else {
      setExpandedCategories(questionCategories.map(c => c.id));
    }
  };

  const addQuickQuestion = (type: QuestionType) => {
    // Find the template for this type
    for (const category of questionCategories) {
      const template = category.templates.find(t => t.type === type);
      if (template) {
        addQuestionToSection(template, selectedSection);
        return;
      }
    }
  };

  const addSection = () => {
    const newSection: SurveySection = {
      id: `section_${Date.now()}`,
      title: `Section ${(survey.sections?.length || 0) + 1}`,
      description: '',
      questions: [],
      order: survey.sections?.length || 0,
    };

    setSurvey(prev => ({
      ...prev,
      sections: [...(prev.sections || []), newSection],
    }));
  };

  const startEditingSection = (sectionId: string, currentTitle: string) => {
    setEditingSectionId(sectionId);
    setEditingSectionTitle(currentTitle);
  };

  const saveEditingSection = () => {
    if (editingSectionId && editingSectionTitle.trim()) {
      setSurvey(prev => ({
        ...prev,
        sections: prev.sections?.map(s =>
          s.id === editingSectionId
            ? { ...s, title: editingSectionTitle.trim() }
            : s
        ),
      }));
    }
    setEditingSectionId(null);
    setEditingSectionTitle('');
  };

  const cancelEditingSection = () => {
    setEditingSectionId(null);
    setEditingSectionTitle('');
  };

  const deleteSection = (sectionId: string) => {
    // Can't delete if it's the only section
    if (survey.sections && survey.sections.length <= 1) {
      alert('Cannot delete the last section. A survey must have at least one section.');
      return;
    }

    setSurvey(prev => ({
      ...prev,
      sections: prev.sections?.filter(s => s.id !== sectionId),
    }));

    // If we deleted the selected section, select the first remaining section
    if (selectedSection === sectionId) {
      const remainingSections = survey.sections?.filter(s => s.id !== sectionId);
      if (remainingSections && remainingSections.length > 0) {
        setSelectedSection(remainingSections[0].id);
      }
    }

    setDeletingSectionId(null);
  };

  // Import functions
  const handleFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      let questions: ParsedQuestion[] = [];

      if (file.name.endsWith('.csv')) {
        // Handle CSV files
        const text = await file.text();
        questions = parseCSV(text);
      } else if (file.name.endsWith('.txt')) {
        // Handle plain text files
        const text = await file.text();
        questions = parseDocument(text);
      } else if (file.name.endsWith('.docx')) {
        // Handle DOCX files using mammoth
        questions = await parseDOCX(file);
      }

      if (questions.length > 0) {
        setParsedImportQuestions(questions);
        setShowImportPreview(true);
        setShowImportMenu(false);
      } else {
        alert('No questions detected in file. Please check the format.');
      }
    } catch (error) {
      console.error('Error importing file:', error);
      alert(`Error importing file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    event.target.value = ''; // Reset input
  };

  const handleImportConfirm = (questions: ParsedQuestion[]) => {
    const questionDefs = convertToQuestionDefinitions(questions);

    setSurvey(prev => ({
      ...prev,
      sections: prev.sections?.map(section =>
        section.id === selectedSection
          ? {
              ...section,
              questions: [...section.questions, ...questionDefs as QuestionDefinition[]],
            }
          : section
      ),
    }));

    setShowImportPreview(false);
    setParsedImportQuestions([]);
  };

  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/survey-template.csv';
    link.download = 'survey-template.csv';
    link.click();
    setShowImportMenu(false);
  };

  const handleBulkLogicApply = (updatedQuestions: QuestionDefinition[]) => {
    setSurvey(prev => ({
      ...prev,
      sections: prev.sections?.map(section =>
        section.id === selectedSection
          ? {
              ...section,
              questions: updatedQuestions
            }
          : section
      ),
    }));
  };

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--color-surface)' }}>
      {/* HEADER - Glassmorphism Design */}
      <div className="flex-shrink-0" style={{
        background: 'var(--glass-bg-card)',
        borderBottom: '1px solid var(--glass-border-light)',
        boxShadow: 'var(--glass-shadow-sm)'
      }}>
        <div className="px-4 py-3">
          {/* Autosave prompt - shown when no title */}
          {(!survey.title || survey.title.trim().length === 0) && (
            <div
              className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg"
              style={{
                backgroundColor: 'rgba(199, 161, 74, 0.12)',
                border: '1px solid rgba(199, 161, 74, 0.3)'
              }}
            >
              <svg style={{ width: '1rem', height: '1rem', color: 'var(--color-accent)', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span style={{ fontSize: '0.8rem', color: 'var(--color-primary)', fontFamily: 'var(--font-body)' }}>
                Enter a survey title to enable autosave
              </span>
            </div>
          )}

          {/* Top row: Title & Meta */}
          <div className="flex items-center gap-4 mb-2">
            <input
              type="text"
              value={survey.title || ''}
              onChange={(e) => setSurvey(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Enter survey title..."
              className="bg-transparent border-none outline-none placeholder-gray-400 min-w-0 flex-shrink"
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.375rem',
                fontWeight: 700,
                color: 'var(--color-primary)',
                width: '280px'
              }}
            />
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded" style={{ backgroundColor: 'rgba(199, 161, 74, 0.1)', color: 'var(--color-accent)' }}>
                <span style={{ fontFamily: 'var(--font-body)', fontWeight: 600 }}>{totalQuestions}</span>
                <span style={{ fontSize: '0.7rem', color: 'var(--color-muted)' }}>Questions</span>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded" style={{ backgroundColor: 'rgba(15, 27, 45, 0.08)', color: 'var(--color-primary)' }}>
                <span style={{ fontFamily: 'var(--font-body)', fontWeight: 600 }}>{survey.sections?.length || 0}</span>
                <span style={{ fontSize: '0.7rem', color: 'var(--color-muted)' }}>Sections</span>
              </div>
              <button
                onClick={() => setShowDescription(!showDescription)}
                className="px-2 py-1 rounded transition-all duration-200 flex items-center gap-1"
                style={{
                  backgroundColor: showDescription ? 'rgba(199, 161, 74, 0.15)' : 'rgba(199, 161, 74, 0.08)',
                  color: 'var(--color-accent)',
                  fontSize: '0.7rem',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 600,
                  border: '1px solid rgba(199, 161, 74, 0.3)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(199, 161, 74, 0.2)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = showDescription ? 'rgba(199, 161, 74, 0.15)' : 'rgba(199, 161, 74, 0.08)'}
                title={showDescription ? "Hide description" : "Show description"}
              >
                <svg
                  className="transition-transform duration-200"
                  style={{
                    width: '0.75rem',
                    height: '0.75rem',
                    transform: showDescription ? 'rotate(0deg)' : 'rotate(-90deg)'
                  }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Description
              </button>

              {/* Autosave Indicator - subtle, only shows after first save */}
              {saveStatus === 'saved' && (
                <div className="flex items-center gap-1 px-2 py-0.5 rounded" style={{
                  color: 'var(--color-muted)',
                  fontSize: '0.65rem',
                  fontFamily: 'var(--font-body)',
                  opacity: 0.6,
                }}>
                  <svg style={{ width: '0.65rem', height: '0.65rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Auto-saved</span>
                </div>
              )}
            </div>
          </div>

          {/* Description - Collapsible */}
          <div className="w-full" style={{ display: showDescription ? 'block' : 'none' }}>
              {/* Formatting Toolbar */}
              <div className="flex items-center gap-1 mb-2 pb-2 border-b" style={{ borderColor: 'var(--color-border)' }}>
                <button
                  type="button"
                  onClick={() => document.execCommand('bold')}
                  className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                  title="Bold"
                  style={{ fontSize: '0.875rem', fontWeight: 'bold' }}
                >
                  <strong>B</strong>
                </button>
                <button
                  type="button"
                  onClick={() => document.execCommand('italic')}
                  className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                  title="Italic"
                  style={{ fontSize: '0.875rem', fontStyle: 'italic' }}
                >
                  <em>I</em>
                </button>
                <button
                  type="button"
                  onClick={() => document.execCommand('underline')}
                  className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                  title="Underline"
                  style={{ fontSize: '0.875rem', textDecoration: 'underline' }}
                >
                  U
                </button>
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                <button
                  type="button"
                  onClick={() => document.execCommand('insertUnorderedList')}
                  className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                  title="Bullet List"
                  style={{ fontSize: '0.875rem' }}
                >
                  •
                </button>
                <button
                  type="button"
                  onClick={() => document.execCommand('insertOrderedList')}
                  className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                  title="Numbered List"
                  style={{ fontSize: '0.875rem' }}
                >
                  1.
                </button>
              </div>

              {/* Rich Text Editor */}
              <div
                ref={descriptionRef}
                contentEditable
                suppressContentEditableWarning
                onPaste={(e) => {
                  e.preventDefault();
                  // Get plain text from clipboard to strip all Word/HTML formatting
                  const text = e.clipboardData.getData('text/plain');
                  document.execCommand('insertText', false, text);
                }}
                onInput={(e) => {
                  const html = e.currentTarget.innerHTML.replace(/&nbsp;/g, ' ');
                  setSurvey(prev => ({ ...prev, description: html }));
                }}
                onBlur={(e) => {
                  const html = e.currentTarget.innerHTML;
                  const cleanText = cleanDescriptionHTML(html);
                  setSurvey(prev => ({ ...prev, description: cleanText }));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    document.execCommand('insertLineBreak');
                  }
                }}
                className="w-full text-sm min-h-[80px] max-h-[200px] overflow-y-auto p-3 rounded border"
                style={{
                  fontFamily: 'var(--font-body)',
                  color: 'var(--color-primary)',
                  backgroundColor: 'white',
                  borderColor: 'var(--color-border)',
                  direction: 'ltr',
                  textAlign: 'left',
                  fontSize: '0.875rem',
                  lineHeight: '1.5'
                }}
                data-placeholder="Add a brief description or introduction (shown at the top of the survey)"
              />
              <style>{`
                [contenteditable][data-placeholder]:empty:before {
                  content: attr(data-placeholder);
                  color: #9CA3AF;
                  font-style: italic;
                }
                [contenteditable]:focus {
                  outline: 2px solid var(--color-accent);
                  outline-offset: -2px;
                }
              `}</style>
          </div>
        </div>

        {/* Tags */}
        <div className="px-6 py-2 flex items-center gap-2 flex-wrap" style={{ borderTop: '1px solid var(--glass-border-light)' }}>
          <span className="text-xs font-medium" style={{ color: 'var(--color-muted)' }}>Tags:</span>
          {(survey.tags || []).map(tag => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: 'rgba(38, 67, 114, 0.08)',
                color: '#264372',
                border: '1px solid rgba(38, 67, 114, 0.15)',
              }}
            >
              {tag}
              <button
                onClick={() => setSurvey(prev => ({
                  ...prev,
                  tags: (prev.tags || []).filter(t => t !== tag)
                }))}
                className="hover:text-red-500 transition-colors"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
          <input
            type="text"
            className="text-xs px-2 py-1 border rounded-lg focus:outline-none focus:ring-1"
            style={{ borderColor: 'var(--glass-border-light)', minWidth: '120px', maxWidth: '200px' }}
            placeholder="Add tag..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                const input = e.target as HTMLInputElement;
                const newTags = input.value.split(',').map(t => t.trim()).filter(t => t.length > 0);
                if (newTags.length > 0) {
                  setSurvey(prev => ({
                    ...prev,
                    tags: Array.from(new Set([...(prev.tags || []), ...newTags]))
                  }));
                  input.value = '';
                }
              }
            }}
          />
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* SIDEBAR - Glassmorphism Design */}
        <div className="w-60 overflow-y-auto flex-shrink-0" style={{
          background: 'var(--glass-bg-subtle)',
          borderRight: '1px solid var(--glass-border-light)'
        }}>
          {/* Sticky Header */}
          <div className="px-4 py-3 sticky top-0 z-10" style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            borderBottom: '1px solid var(--glass-border-light)'
          }}>
            <div className="flex items-center justify-between">
              <h3 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '0.8125rem',
                fontWeight: 700,
                color: 'var(--color-primary)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em'
              }}>
                Question Types
              </h3>
              <button
                onClick={toggleAllCategories}
                className="w-6 h-6 rounded-md flex items-center justify-center transition-all"
                style={{
                  fontSize: '0.875rem',
                  color: 'var(--color-accent)',
                  background: 'transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(199, 161, 74, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
                title={expandedCategories.length === questionCategories.length ? "Collapse All" : "Expand All"}
              >
                {expandedCategories.length === questionCategories.length ? "−" : "+"}
              </button>
            </div>
          </div>

          {/* Categories */}
          <div className="p-3 space-y-3">
            {questionCategories.map((category) => (
              <div key={category.id} className="space-y-2">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200"
                  style={{
                    background: isCategoryExpanded(category.id)
                      ? 'rgba(199, 161, 74, 0.08)'
                      : 'transparent',
                    border: '1px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(199, 161, 74, 0.12)';
                    e.currentTarget.style.borderColor = 'rgba(199, 161, 74, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isCategoryExpanded(category.id)
                      ? 'rgba(199, 161, 74, 0.08)'
                      : 'transparent';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span style={{ fontSize: '0.9rem' }}>{category.icon}</span>
                    <span style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: 'var(--color-primary)'
                    }}>
                      {category.name}
                    </span>
                    <span style={{
                      fontSize: '0.65rem',
                      color: 'var(--color-muted)',
                      fontFamily: 'var(--font-body)',
                      background: 'rgba(0,0,0,0.05)',
                      padding: '0.125rem 0.375rem',
                      borderRadius: '9999px'
                    }}>
                      {category.templates.length}
                    </span>
                  </div>
                  <svg
                    className="transition-transform duration-200"
                    style={{
                      width: '0.75rem',
                      height: '0.75rem',
                      color: 'var(--color-muted)',
                      transform: isCategoryExpanded(category.id) ? 'rotate(90deg)' : 'rotate(0deg)'
                    }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {/* Question Templates */}
                {isCategoryExpanded(category.id) && (
                  <div className="ml-2 space-y-1.5">
                    {category.templates.map((template) => (
                        <div
                          key={template.id}
                          draggable
                          onDragStart={(e) => {
                            setIsDragging(true);
                            setDraggingType(template.type);
                            e.dataTransfer.setData('application/json', JSON.stringify({
                              type: 'template',
                              template,
                            }));
                          }}
                          onDragEnd={() => {
                            setIsDragging(false);
                            setDraggingType(null);
                          }}
                          onClick={() => addQuestionToSection(template, selectedSection)}
                          className="group flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-grab transition-all duration-200"
                          style={{
                            background: 'white',
                            border: '1px solid var(--color-border)',
                            opacity: isDragging && draggingType === template.type ? 0.5 : 1,
                            transform: isDragging && draggingType === template.type ? 'scale(0.95)' : 'scale(1)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = 'var(--color-accent)';
                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.08)';
                            e.currentTarget.style.transform = 'translateX(4px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'var(--color-border)';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          {/* Icon */}
                          <div
                            className="flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center"
                            style={{
                              background: 'var(--color-primary)',
                              fontSize: '0.875rem'
                            }}
                          >
                            <span>{template.icon}</span>
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div style={{
                              fontFamily: 'var(--font-body)',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: 'var(--color-primary)'
                            }} className="truncate">
                              {template.name}
                            </div>
                          </div>

                          {/* Drag/Click indicator */}
                          <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--color-accent)' }}>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </div>
                        </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Builder Area - Glassmorphism Design */}
        <div className="flex-1 flex flex-col overflow-hidden">
            {/* Section Tabs - Pill Style */}
            <div className="px-4 py-3 flex-shrink-0" style={{
              background: 'var(--glass-bg-subtle)',
              borderBottom: '1px solid var(--glass-border-light)'
            }}>
              {/* Section tabs - wrapping */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Section Tabs */}
                {survey.sections?.map((section, index) => (
                  <div key={section.id} className="flex items-center gap-1">
                    {editingSectionId === section.id ? (
                      /* Editing Mode - Glass Styled */
                      <div className="flex items-center gap-1 px-2 py-1 rounded-lg" style={{
                        background: 'rgba(199, 161, 74, 0.1)',
                        border: '1px solid var(--color-accent)'
                      }}>
                        <input
                          type="text"
                          value={editingSectionTitle}
                          onChange={(e) => setEditingSectionTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEditingSection();
                            if (e.key === 'Escape') cancelEditingSection();
                          }}
                          className="w-32 px-2 py-1 text-xs font-medium rounded-md focus:outline-none"
                          style={{
                            background: 'white',
                            border: '1px solid var(--glass-border-light)',
                            color: 'var(--color-primary)'
                          }}
                          autoFocus
                        />
                        <button
                          onClick={saveEditingSection}
                          className="p-1 rounded-md transition-colors"
                          style={{ color: 'var(--color-success)' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(34, 197, 94, 0.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          title="Save"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          onClick={cancelEditingSection}
                          className="p-1 rounded-md transition-colors"
                          style={{ color: 'var(--color-danger)' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          title="Cancel"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      /* Display Mode - Pill Style Tabs */
                      <div className="flex items-center rounded-lg overflow-hidden" style={{
                        background: selectedSection === section.id ? 'var(--glass-bg-card)' : 'transparent',
                        border: selectedSection === section.id ? '1px solid var(--color-accent)' : '1px solid transparent',
                        boxShadow: selectedSection === section.id ? 'var(--glass-shadow-sm)' : 'none'
                      }}>
                        <button
                          onClick={() => setSelectedSection(section.id)}
                          className="px-3 py-2 transition-all duration-200"
                          style={{
                            fontFamily: 'var(--font-body)',
                            fontSize: '0.8125rem',
                            fontWeight: selectedSection === section.id ? 600 : 500,
                            background: 'transparent',
                            color: selectedSection === section.id ? 'var(--color-primary)' : 'var(--color-muted)',
                            borderBottom: selectedSection === section.id ? '2px solid var(--color-accent)' : '2px solid transparent'
                          }}
                          onMouseEnter={(e) => {
                            if (selectedSection !== section.id) {
                              e.currentTarget.style.color = 'var(--color-primary)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedSection !== section.id) {
                              e.currentTarget.style.color = 'var(--color-muted)';
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <span>{section.title}</span>
                            {section.questions.length > 0 && (
                              <span className="px-1.5 py-0.5 rounded-full" style={{
                                fontSize: '0.65rem',
                                fontFamily: 'var(--font-body)',
                                fontWeight: 600,
                                background: selectedSection === section.id ? 'var(--gradient-accent-btn)' : 'rgba(0,0,0,0.06)',
                                color: selectedSection === section.id ? 'var(--color-primary)' : 'var(--color-muted)'
                              }}>
                                {section.questions.length}
                              </span>
                            )}
                          </div>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditingSection(section.id, section.title);
                          }}
                          className="px-2 py-2 transition-all duration-200"
                          style={{
                            background: 'transparent',
                            color: selectedSection === section.id ? 'var(--color-muted)' : 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = 'var(--color-accent)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = selectedSection === section.id ? 'var(--color-muted)' : 'transparent';
                          }}
                          title="Edit section name"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        {survey.sections && survey.sections.length > 1 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletingSectionId(section.id);
                            }}
                            className="px-2 py-2 transition-all duration-200"
                            style={{
                              background: 'transparent',
                              color: selectedSection === section.id ? 'var(--color-muted)' : 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = 'var(--color-danger)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = selectedSection === section.id ? 'var(--color-muted)' : 'transparent';
                            }}
                            title="Delete section"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}

              </div>

              {/* Actions row */}
              <div className="flex items-center gap-2 mt-2">
                {/* Add Section Button */}
                <button
                  onClick={addSection}
                  className="px-3 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5"
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: 'var(--color-accent)',
                    background: 'transparent',
                    border: '1px dashed var(--color-accent)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(199, 161, 74, 0.1)';
                    e.currentTarget.style.borderStyle = 'solid';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderStyle = 'dashed';
                  }}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Section
                </button>

                {/* Spacer */}
                <div className="flex-1"></div>

                {/* Import Button with Dropdown */}
                <div className="relative">
                  <button
                    ref={importButtonRef}
                    onClick={() => setShowImportMenu(!showImportMenu)}
                    className="px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5"
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      background: 'white',
                      color: 'var(--color-primary)',
                      border: '1px solid var(--color-border)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-accent)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-border)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Import
                  </button>

                  {/* Import Dropdown Menu */}
                  {showImportMenu && importButtonRef.current && (
                    <>
                      <div
                        className="fixed inset-0 z-[9998]"
                        onClick={() => setShowImportMenu(false)}
                      />
                      <div
                        className="fixed bg-white rounded-lg shadow-2xl border border-gray-200 py-1 z-[9999] whitespace-nowrap"
                        style={{
                          top: `${importButtonRef.current.getBoundingClientRect().bottom + 4}px`,
                          left: `${importButtonRef.current.getBoundingClientRect().left}px`
                        }}
                      >
                        <label className="block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer transition-colors">
                          <input
                            type="file"
                            accept=".csv,.txt,.docx"
                            onChange={handleFileImport}
                            className="hidden"
                          />
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <span>Import from File</span>
                          </div>
                        </label>
                        <button
                          onClick={downloadTemplate}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            <span>Download Template</span>
                          </div>
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Bulk Logic Button */}
                <button
                  onClick={() => setShowBulkLogicEditor(true)}
                  className="px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5"
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    background: 'white',
                    color: 'var(--color-primary)',
                    border: '1px solid var(--color-border)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-accent)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <span style={{ fontSize: '1rem' }}>⚡</span>
                  Bulk Logic
                </button>
              </div>
            </div>

            {/* Questions Area - COMPACT & SCROLLABLE */}
            <div className="flex-1 overflow-y-auto p-3">
              {currentSection && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                >
                  {currentSection.questions.length === 0 ? (
                    /* Empty State - Glassmorphism Design */
                    <div className="h-full flex items-center justify-center p-4">
                      <div className="relative">
                        {/* Subtle Background */}
                        <div className="absolute inset-0 rounded-3xl opacity-30" style={{
                          background: 'linear-gradient(135deg, rgba(199, 161, 74, 0.1) 0%, rgba(15, 27, 45, 0.05) 100%)'
                        }} />

                        {/* Content Container */}
                        <div
                          className="relative rounded-3xl p-12 sm:p-16 text-center transition-all duration-300"
                          style={{
                            background: isDragOverMainArea ? 'rgba(199, 161, 74, 0.05)' : 'white',
                            border: isDragOverMainArea ? '2px dashed var(--color-accent)' : '2px dashed var(--color-border)',
                            transform: isDragOverMainArea ? 'scale(1.01)' : 'scale(1)'
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const data = JSON.parse(e.dataTransfer.getData('application/json'));
                            if (data.type === 'template') {
                              addQuestionToSection(data.template, selectedSection);
                            }
                            setIsDragOverMainArea(false);
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragOverMainArea(true);
                          }}
                          onDragLeave={() => setIsDragOverMainArea(false)}
                        >
                          {/* Icon */}
                          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center" style={{
                            background: 'var(--color-accent)',
                            boxShadow: '0 8px 16px rgba(199, 161, 74, 0.25)'
                          }}>
                            <svg className="w-10 h-10" fill="none" stroke="white" viewBox="0 0 24 24" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                            </svg>
                          </div>

                          {/* Heading */}
                          <h3 style={{
                            fontFamily: 'var(--font-heading)',
                            fontSize: '1.875rem',
                            fontWeight: 600,
                            color: 'var(--color-primary)',
                            marginBottom: '1rem'
                          }}>
                            Start Building Your Survey
                          </h3>

                          {/* Description */}
                          <p style={{
                            fontFamily: 'var(--font-body)',
                            fontSize: '1rem',
                            color: 'var(--color-muted)',
                            maxWidth: '40rem',
                            margin: '0 auto 2.5rem',
                            lineHeight: '1.6'
                          }}>
                            Drag question types from the left sidebar or use quick-add buttons below.
                          </p>

                          {/* Quick Add Buttons */}
                          <div className="flex flex-wrap justify-center gap-3 mb-10">
                            <button
                              onClick={() => addQuickQuestion('text')}
                              className="transition-all duration-200 rounded-lg"
                              style={{
                                background: 'white',
                                border: '1px solid var(--color-border)',
                                color: 'var(--color-primary)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.75rem 1.25rem'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--color-accent)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--color-border)';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                            >
                              <span style={{ fontSize: '1.125rem' }}>📝</span>
                              <span style={{ fontFamily: 'var(--font-body)', fontWeight: 600 }}>Add Text Question</span>
                            </button>

                            <button
                              onClick={() => addQuickQuestion('radio')}
                              className="transition-all duration-200 rounded-lg"
                              style={{
                                background: 'white',
                                border: '1px solid var(--color-border)',
                                color: 'var(--color-primary)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.75rem 1.25rem'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--color-accent)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--color-border)';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                            >
                              <span style={{ fontSize: '1.125rem' }}>🔘</span>
                              <span style={{ fontFamily: 'var(--font-body)', fontWeight: 600 }}>Add Multiple Choice</span>
                            </button>

                            <button
                              onClick={() => addQuickQuestion('rating')}
                              className="transition-all duration-200 rounded-lg"
                              style={{
                                background: 'white',
                                border: '1px solid var(--color-border)',
                                color: 'var(--color-primary)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.75rem 1.25rem'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--color-accent)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--color-border)';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                            >
                              <span style={{ fontSize: '1.125rem' }}>⭐</span>
                              <span style={{ fontFamily: 'var(--font-body)', fontWeight: 600 }}>Add Rating</span>
                            </button>
                          </div>

                          {/* Drag hint */}
                          <div className="pt-6" style={{ borderTop: '1px solid var(--glass-border-light)' }}>
                            <p style={{
                              fontFamily: 'var(--font-body)',
                              fontSize: '0.875rem',
                              color: 'var(--color-muted)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem'
                            }}>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                              </svg>
                              You can also drag question types from the sidebar
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Questions List */
                    <div className="max-w-7xl mx-auto px-8 py-6">
                      <div
                        onDrop={(e) => {
                          e.preventDefault();
                          const data = JSON.parse(e.dataTransfer.getData('application/json'));
                          if (data.type === 'template') {
                            addQuestionToSection(data.template, selectedSection);
                          }
                          setIsDragOverMainArea(false);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setIsDragOverMainArea(true);
                        }}
                        onDragLeave={() => setIsDragOverMainArea(false)}
                        className={`drop-zone relative ${
                          isDragOverMainArea ? 'drag-over' : ''
                        }`}
                      >
                        {/* Drag Overlay - IMPROVED */}
                        {isDragOverMainArea && (
                          <div className="drop-zone-overlay">
                            <div className="drop-indicator">
                              <div className="flex items-center gap-3">
                                <svg className="w-8 h-8 text-blue-600 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                </svg>
                                <span className="text-xl font-bold text-gray-900">Drop question here</span>
                                <svg className="w-8 h-8 text-blue-600 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        )}

                        <SortableContext
                          items={currentSection.questions.map(q => q.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {currentSection.questions.map((question, index) => (
                            <DraggableQuestion
                              key={question.id}
                              question={question}
                              questionNumber={index + 1}
                              sectionId={selectedSection}
                              onEdit={editQuestion}
                              onDelete={deleteQuestion}
                              getQuestionIcon={getQuestionIcon}
                              isJustDropped={justDroppedId === question.id}
                              animationIndex={index}
                            />
                          ))}
                        </SortableContext>
                      </div>
                    </div>
                  )}
                </DndContext>
              )}
            </div>
        </div>
      </div>

      {/* Question Editor Modal */}
      {showQuestionEditor && editingQuestion && (
        <QuestionEditorModal
          question={editingQuestion}
          isOpen={showQuestionEditor}
          onClose={() => {
            setEditingQuestion(null);
            setShowQuestionEditor(false);
          }}
          onSave={saveQuestion}
          onDelete={deleteQuestion}
          questionIcon={getQuestionIcon(editingQuestion.type)}
          allQuestions={currentSection?.questions || []}
        />
      )}

      {/* Import Preview Modal */}
      {showImportPreview && (
        <ImportPreview
          questions={parsedImportQuestions}
          onConfirm={handleImportConfirm}
          onCancel={() => {
            setShowImportPreview(false);
            setParsedImportQuestions([]);
          }}
        />
      )}

      {/* Delete Section Confirmation Dialog */}
      {deletingSectionId && (
        <ConfirmDialog
          isOpen={true}
          title="Delete Section"
          message={`Are you sure you want to delete "${survey.sections?.find(s => s.id === deletingSectionId)?.title}"? ${
            (survey.sections?.find(s => s.id === deletingSectionId)?.questions.length || 0) > 0
              ? `This will permanently delete ${survey.sections?.find(s => s.id === deletingSectionId)?.questions.length} question(s).`
              : 'This action cannot be undone.'
          }`}
          confirmText="Delete Section"
          cancelText="Cancel"
          onConfirm={() => deleteSection(deletingSectionId)}
          onCancel={() => setDeletingSectionId(null)}
          type="danger"
        />
      )}

      {/* Bulk Logic Editor Modal */}
      {showBulkLogicEditor && currentSection && (
        <BulkLogicEditor
          questions={currentSection.questions}
          onApply={handleBulkLogicApply}
          onClose={() => setShowBulkLogicEditor(false)}
        />
      )}
    </div>
  );
});

SurveyBuilder.displayName = 'SurveyBuilder';

export default SurveyBuilder;